import React, { useEffect, useMemo, useState } from 'react';
import {
  BillingInterval,
  chargeLine,
  money,
  planPrice,
  TinycmsPlan,
} from '../account/tinycmsPlans';

/**
 * Change plan, for a TinyCMS account in Plan & billing ("TinyCMS UX
 * optimization review v4.zip", Prototype v2 → Plan & billing): the four plans
 * as rows under a Monthly / Yearly switch, the current one marked, and — once
 * a different plan or billing is picked — what happens and when, what the
 * account gains or loses, and which card is charged.
 *
 * The summary says exactly what Stripe will do (changeTinycmsPlan on the
 * server): an upgrade is charged the prorated difference today, a downgrade is
 * credited on the next bill, a trial keeps its end date, Free starts when the
 * paid period ends, and a site that was never billed starts paying today.
 */

export interface PlanBilling {
  /** trialing | active | past_due | canceled | grandfathered | none … */
  status: string;
  periodEnd?: string | null;
  trialEnd?: string | null;
  cancelAtPeriodEnd: boolean;
}

const day = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString([], { month: 'long', day: 'numeric' })
    : 'the end of this period';

interface Props {
  plans: TinycmsPlan[];
  /** The account's plan and billing now. */
  currentKey: string;
  currentInterval: BillingInterval;
  /** The default card's last four digits, if there is one. */
  cardLast4?: string | null;
  billing?: PlanBilling | null;
  saving: boolean;
  error: string;
  onConfirm: (planKey: string, interval: BillingInterval) => void;
  onAddCard: () => void;
  onClose: () => void;
}

/** "Everything in Free" restates the plan below; the real differences are the rest. */
const ownFeatures = (plan: TinycmsPlan) => plan.features.filter((f) => !/^everything in /i.test(f));

export function TinycmsChangePlan({
  plans,
  currentKey,
  currentInterval,
  cardLast4,
  billing,
  saving,
  error,
  onConfirm,
  onAddCard,
  onClose,
}: Props) {
  const [planKey, setPlanKey] = useState(currentKey);
  const [interval, setInterval] = useState<BillingInterval>(currentInterval);

  // Escape closes, as every Ink modal does.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  const rank = (key: string) => plans.findIndex((p) => p.key === key);
  const current = plans.find((p) => p.key === currentKey);
  const selected = plans.find((p) => p.key === planKey);

  const summary = useMemo(() => {
    if (!selected) return null;
    const changed = planKey !== currentKey || interval !== currentInterval;
    const status = billing?.status || 'none';
    const live = ['trialing', 'active', 'past_due'].includes(status);
    // Choosing the current plan again undoes a cancel.
    const resume = !changed && live && Boolean(billing?.cancelAtPeriodEnd);
    if (!changed && !resume) return null;
    const from = rank(currentKey);
    const to = rank(planKey);
    // Features of every plan between the two, on the side that's moving away.
    const between = (lo: number, hi: number) =>
      plans.filter((_, i) => i > lo && i <= hi).flatMap(ownFeatures);
    const lose = to < from ? between(to, from) : [];
    const gain = to > from ? between(from, to) : [];
    const price = planPrice(selected, interval);
    const first = money(price * (interval === 'year' ? 12 : 1));
    const yearly = (p: TinycmsPlan | undefined, i: BillingInterval) =>
      p ? planPrice(p, i) * 12 : 0;
    const upgrade = yearly(selected, interval) > yearly(current, currentInterval);
    let when: string;
    if (resume) {
      when = `Your ${selected.label} plan continues and renews on ${day(billing?.periodEnd)}, at ${chargeLine(selected, interval)}.`;
    } else if (!price) {
      when =
        status === 'trialing'
          ? `Your trial ends on ${day(billing?.trialEnd)} and your site moves to Free then. You won’t be charged.`
          : live
            ? `You keep ${current?.label || 'your plan'} until ${day(billing?.periodEnd)}, then your site moves to Free. You won’t be charged again.`
            : 'Your site moves to Free now.';
    } else if (status === 'trialing') {
      when = `${selected.label} starts now. Your trial still ends on ${day(billing?.trialEnd)}; then you’re billed ${chargeLine(selected, interval)}.`;
    } else if (live) {
      when = upgrade
        ? `${selected.label} starts now. You’re charged the difference for the rest of this billing period today, then ${chargeLine(selected, interval)}.`
        : `${selected.label} starts now. What’s left of ${current?.label || 'your plan'} is credited to your next bill; then ${chargeLine(selected, interval)}.`;
    } else if (status === 'grandfathered') {
      when = `${selected.label} starts now and you’re charged ${first} today, then ${chargeLine(selected, interval)}. Your current plan hasn’t been billed so far; this starts billing.`;
    } else {
      when = `${selected.label} starts now and you’re charged ${first} today, then ${chargeLine(selected, interval)}.`;
    }
    return { lose, gain, price, when, resume };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plans, planKey, interval, currentKey, currentInterval, selected, billing]);

  const needsCard = Boolean(summary?.price) && !cardLast4;
  const canConfirm = Boolean(summary) && !needsCard && !saving;

  return (
    <div
      className="ink-modal-back cp-back"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !saving) onClose();
      }}
    >
      <div className="cp" role="dialog" aria-modal="true" aria-labelledby="cp-title">
        <div className="cp-head">
          <div>
            <div className="cp-title" id="cp-title">
              Change plan
            </div>
            {current ? (
              <div className="cp-sub">
                You’re on {current.label}
                {planPrice(current, currentInterval)
                  ? `, billed ${currentInterval === 'year' ? 'yearly' : 'monthly'}`
                  : ''}
                .
              </div>
            ) : null}
          </div>
          <button type="button" className="cp-close" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="cp-seg" role="radiogroup" aria-label="Billing">
          {(['month', 'year'] as BillingInterval[]).map((v) => (
            <button
              key={v}
              type="button"
              role="radio"
              aria-checked={interval === v}
              data-active={interval === v ? 'true' : undefined}
              onClick={() => setInterval(v)}
            >
              {v === 'month' ? 'Monthly' : 'Yearly'}
              {v === 'year' ? <span className="cp-save">Save 25%</span> : null}
            </button>
          ))}
        </div>

        <div className="cp-rows" role="radiogroup" aria-label="Plan">
          {plans.map((p) => {
            const on = p.key === planKey;
            return (
              <div
                key={p.key}
                role="radio"
                aria-checked={on}
                tabIndex={0}
                className="cp-row"
                data-active={on ? 'true' : undefined}
                onClick={() => setPlanKey(p.key)}
                onKeyDown={(e) => {
                  if (e.key === ' ' || e.key === 'Enter') {
                    e.preventDefault();
                    setPlanKey(p.key);
                  }
                }}
              >
                <span className="cp-radio" />
                <div className="cp-row-text">
                  <div className="cp-row-name">
                    {p.label}
                    {p.key === currentKey ? <span className="cp-current">Current</span> : null}
                  </div>
                  <div className="cp-row-summary">{p.summary}</div>
                </div>
                <div className="cp-row-price">
                  {money(planPrice(p, interval))}
                  <span>/mo</span>
                </div>
              </div>
            );
          })}
        </div>

        {summary ? (
          <div className="cp-summary">
            <div className="cp-when">{summary.when}</div>
            {summary.lose.length ? (
              <>
                <div className="cp-list-head">You’ll lose</div>
                <ul className="cp-list">
                  {summary.lose.map((f) => (
                    <li key={f} data-kind="lose">
                      {f}
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
            {summary.gain.length ? (
              <ul className="cp-list">
                {summary.gain.map((f) => (
                  <li key={f} data-kind="gain">
                    {f}
                  </li>
                ))}
              </ul>
            ) : null}
            {summary.price ? (
              cardLast4 ? (
                <div className="cp-card">Charged to the card ending {cardLast4}</div>
              ) : (
                <div className="cp-card">
                  Add a card to switch to a paid plan.{' '}
                  <button type="button" className="ink-bill-link" onClick={onAddCard}>
                    Add card
                  </button>
                </div>
              )
            ) : null}
          </div>
        ) : null}

        {error ? <p className="cp-error">{error}</p> : null}

        <div className="cp-actions">
          <button type="button" className="cp-cancel" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="ink-btn ink-btn-primary cp-confirm"
            disabled={!canConfirm}
            onClick={() => onConfirm(planKey, interval)}
          >
            {saving
              ? 'Switching…'
              : !summary || !selected
                ? 'Choose a plan'
                : summary.resume
                  ? `Keep ${selected.label}`
                  : planKey === currentKey
                    ? `Switch to ${interval === 'year' ? 'yearly' : 'monthly'} billing`
                    : `Switch to ${selected.label}`}
          </button>
        </div>
      </div>
    </div>
  );
}
